import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import Layout from "@/components/Layout";
import ConfirmDialog from "@/components/ConfirmDialog";
import { api } from "@/lib/api";
import { fmtDate } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Plus, X, Trash2, ShieldCheck, User as UserIcon, ShieldAlert } from "lucide-react";

export default function Users() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [agents, setAgents] = useState(["", "", "", ""]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/users");
      setRows(data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Kullanıcılar yüklenemedi");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (isAdmin) {
      load();
      api.get("/settings/assignment").then((res) => {
        const a = res.data.agents || [];
        setAgents([a[0] || "", a[1] || "", a[2] || "", a[3] || ""]);
      }).catch(() => {});
    } else setLoading(false);
  }, [load, isAdmin]);

  const saveAgents = async () => {
    try { await api.put("/settings/assignment", { agents: agents.filter(Boolean) }); toast.success("Görev dağıtımı kaydedildi"); }
    catch (e) { toast.error(e.response?.data?.detail || "Kaydedilemedi"); }
  };

  const changeRole = async (u, role) => {
    try {
      await api.put(`/users/${u.id}/role`, { role });
      toast.success("Rol güncellendi");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Rol güncellenemedi"); }
  };

  const toggleCariYetkili = async (u) => {
    try {
      await api.put(`/users/${u.id}/cari-yetkili`, { cari_yetkili: !u.cari_yetkili });
      toast.success(!u.cari_yetkili ? "Cari yetkili atama rolü verildi" : "Cari yetkili rolü kaldırıldı");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Güncellenemedi"); }
  };

  const remove = (u) => {
    setConfirm({
      title: "Kullanıcıyı Sil",
      description: `${u.email} kullanıcısını silmek istediğinize emin misiniz?`,
      onConfirm: async () => {
        try { await api.delete(`/users/${u.id}`); toast.success("Kullanıcı silindi"); load(); }
        catch (e) { toast.error(e.response?.data?.detail || "Silinemedi"); }
        finally { setConfirm(null); }
      },
    });
  };

  if (!isAdmin) {
    return (
      <Layout>
        <div className="flex flex-col items-center justify-center py-24 text-center" data-testid="users-no-access">
          <ShieldAlert className="w-10 h-10 text-[#A63A3A] mb-3" />
          <h2 className="font-heading text-xl font-bold">Yetkiniz Yok</h2>
          <p className="text-sm text-[#8C8377] mt-1">Bu sayfa yalnızca yöneticiler içindir.</p>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="kicker mb-1">YÖNETİM</div>
          <h1 className="font-heading text-3xl font-bold tracking-tight">Kullanıcı Yönetimi</h1>
        </div>
        <button onClick={() => setShowForm(true)} data-testid="add-user-button" className="flex items-center gap-1.5 px-4 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150">
          <Plus className="w-4 h-4" /> Yeni Kullanıcı
        </button>
      </div>

      <div className="bg-white border border-[#E7DECC] rounded-sm overflow-auto max-h-[calc(100vh-20rem)]" data-testid="users-table">
        <table className="w-full text-sm">
          <thead className="sticky top-0 z-10 bg-[#F3ECDD]">
            <tr className="border-b border-[#E7DECC] bg-[#F3ECDD]">
              <th className="px-3 py-2.5 kicker text-left">Ad</th>
              <th className="px-3 py-2.5 kicker text-left">E-posta</th>
              <th className="px-3 py-2.5 kicker text-left">Rol</th>
              <th className="px-3 py-2.5 kicker text-left">Şifre</th>
              <th className="px-3 py-2.5 kicker text-left">Kayıt</th>
              <th className="px-3 py-2.5 kicker text-right">İşlem</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={6} className="px-3 py-10 text-center text-[#8C8377]">Yükleniyor...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={6} className="px-3 py-10 text-center text-[#8C8377]" data-testid="users-empty">Kullanıcı yok.</td></tr>
            ) : rows.map((u, i) => {
              const selfId = user?.id || user?._id;
              const self = u.id === selfId;
              return (
                <tr key={u.id} className={`border-b border-[#E7DECC] hover:bg-[#EFE7D6] transition-colors duration-150 ${i % 2 ? "bg-[#F3ECDD]/60" : ""}`} data-testid={`user-row-${u.id}`}>
                  <td className="px-3 py-2 font-medium whitespace-nowrap">{u.name} {self && <span className="text-xs text-[#8C8377]">(siz)</span>}</td>
                  <td className="px-3 py-2 whitespace-nowrap">{u.email}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {u.role === "admin" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-semibold bg-[#B8933F]/10 text-[#B8933F]"><ShieldCheck className="w-3.5 h-3.5" /> Yönetici</span>
                    ) : u.role === "katip" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-semibold bg-[#7C5E9B]/10 text-[#7C5E9B]"><ShieldCheck className="w-3.5 h-3.5" /> Katip</span>
                    ) : u.role === "avukat" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-semibold bg-[#0A84FF]/10 text-[#0A84FF]"><ShieldCheck className="w-3.5 h-3.5" /> Avukat</span>
                    ) : u.role === "izleyici" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-semibold bg-[#B8933F]/10 text-[#B8933F]"><UserIcon className="w-3.5 h-3.5" /> İzleyici</span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-xs font-semibold bg-[#8C8377]/10 text-[#8C8377]"><UserIcon className="w-3.5 h-3.5" /> Kullanıcı</span>
                    )}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap" data-testid={`user-pw-${u.id}`}>
                    <button onClick={async () => { const np = window.prompt(`${u.name} için yeni şifre (en az 6 karakter):`); if (np === null) return; if (np.length < 6) { toast.error("Şifre en az 6 karakter olmalı"); return; } try { await api.put(`/users/${u.id}/reset-password`, { new_password: np }); toast.success("Şifre güncellendi"); } catch (e) { toast.error(e.response?.data?.detail || "Sıfırlanamadı"); } }} data-testid={`user-reset-pw-${u.id}`} className="px-2 py-1 text-xs font-semibold text-[#B8933F] hover:bg-[#B8933F]/10 rounded-sm transition-colors duration-150">Şifre Sıfırla</button>
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap tabular-nums text-[#8C8377]">{fmtDate(u.created_at)}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    <div className="flex justify-end items-center gap-2">
                      <select
                        value={u.role}
                        disabled={self}
                        onChange={(e) => changeRole(u, e.target.value)}
                        data-testid={`user-role-${u.id}`}
                        className="px-2 py-1 border border-[#E7DECC] bg-white rounded-sm text-xs focus:outline-none focus:border-[#B8933F] disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <option value="user">Kullanıcı</option>
                        <option value="admin">Yönetici</option>
                        <option value="katip">Katip</option>
                        <option value="avukat">Avukat</option>
                        <option value="izleyici">İzleyici / Patron</option>
                      </select>
                      <button
                        onClick={() => toggleCariYetkili(u)}
                        data-testid={`user-cari-yetkili-${u.id}`}
                        title="Cari müşterilere yetkili olarak atanabilsin mi?"
                        className={`px-2 py-1 rounded-sm text-xs font-semibold transition-colors duration-150 border ${u.cari_yetkili ? "bg-[#2F6E4E]/10 text-[#2F6E4E] border-[#2F6E4E]/30" : "bg-white text-[#8C8377] border-[#E7DECC] hover:bg-[#EFE7D6]"}`}
                      >
                        {u.cari_yetkili ? "✓ Cari Yetkili" : "Cari Yetkili"}
                      </button>
                      <button
                        onClick={() => remove(u)}
                        disabled={self}
                        data-testid={`user-delete-${u.id}`}
                        title={self ? "Kendinizi silemezsiniz" : "Sil"}
                        className="p-1.5 text-[#8C8377] hover:text-[#A63A3A] hover:bg-white rounded-sm transition-colors duration-150 disabled:opacity-40 disabled:cursor-not-allowed"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="mt-6 bg-white border border-[#E7DECC] rounded-sm" data-testid="assignment-settings">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#E7DECC]">
          <div className="kicker">GÖREV DAĞITIMI (SIRALI ROTASYON)</div>
          <button onClick={saveAgents} data-testid="assignment-save" className="px-3 py-1.5 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150">Kaydet</button>
        </div>
        <div className="p-4 grid grid-cols-1 md:grid-cols-4 gap-3">
          {[0, 1, 2, 3].map((i) => (
            <div key={i}>
              <label className="kicker block mb-1.5">{i + 1}. ÇALIŞAN</label>
              <select value={agents[i] || ""} onChange={(e) => setAgents((a) => { const n = [...a]; n[i] = e.target.value; return n; })} data-testid={`assignment-agent-${i}`} className="w-full px-2 py-2 border border-[#E7DECC] bg-white rounded-sm text-sm focus:outline-none focus:border-[#B8933F]">
                <option value="">— Seçin —</option>
                {rows.map((u) => <option key={u.id} value={u.email}>{u.name} ({u.email})</option>)}
              </select>
            </div>
          ))}
        </div>
        <div className="px-4 pb-4 text-xs text-[#8C8377]">Web sitesinden gelen her başvuru bu çalışanlara sırayla otomatik atanır.</div>
      </div>

      {showForm && <UserForm onClose={() => setShowForm(false)} onDone={() => { setShowForm(false); load(); }} />}
      <ConfirmDialog
        open={!!confirm}
        onOpenChange={(o) => { if (!o) setConfirm(null); }}
        title={confirm?.title}
        description={confirm?.description}
        onConfirm={confirm?.onConfirm}
      />
    </Layout>
  );
}

function UserForm({ onClose, onDone }) {
  const [form, setForm] = useState({ name: "", email: "", password: "", role: "user" });
  const [saving, setSaving] = useState(false);
  const change = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.password) { toast.error("Ad, e-posta ve şifre zorunlu"); return; }
    if (form.password.length < 6) { toast.error("Şifre en az 6 karakter olmalı"); return; }
    setSaving(true);
    try {
      await api.post("/users", form);
      toast.success("Kullanıcı oluşturuldu");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Kullanıcı oluşturulamadı"); }
    finally { setSaving(false); }
  };
  return (
    <div className="fixed inset-0 z-[100] flex items-start justify-center bg-black/40 p-4 overflow-y-auto">
      <div className="bg-white border border-[#E7DECC] rounded-sm w-full max-w-md my-16" data-testid="user-form">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#E7DECC]">
          <h3 className="font-heading text-xl font-bold tracking-tight">Yeni Kullanıcı</h3>
          <button onClick={onClose} data-testid="user-form-close" aria-label="Kapat" className="text-[#8C8377] hover:text-[#26231E]"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-4">
          <div>
            <label className="kicker block mb-1.5">AD SOYAD</label>
            <input value={form.name} onChange={(e) => change("name", e.target.value)} data-testid="user-field-name" className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm focus:outline-none focus:border-[#B8933F]" />
          </div>
          <div>
            <label className="kicker block mb-1.5">E-POSTA</label>
            <input type="email" value={form.email} onChange={(e) => change("email", e.target.value)} data-testid="user-field-email" className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm focus:outline-none focus:border-[#B8933F]" />
          </div>
          <div>
            <label className="kicker block mb-1.5">ŞİFRE</label>
            <input type="password" value={form.password} onChange={(e) => change("password", e.target.value)} data-testid="user-field-password" className="w-full px-3 py-2 border border-[#E7DECC] rounded-sm focus:outline-none focus:border-[#B8933F]" placeholder="En az 6 karakter" />
          </div>
          <div>
            <label className="kicker block mb-1.5">ROL</label>
            <select value={form.role} onChange={(e) => change("role", e.target.value)} data-testid="user-field-role" className="w-full px-3 py-2 border border-[#E7DECC] bg-white rounded-sm focus:outline-none focus:border-[#B8933F]">
              <option value="user">Kullanıcı</option>
              <option value="admin">Yönetici</option>
              <option value="katip">Katip</option>
              <option value="avukat">Avukat</option>
              <option value="izleyici">İzleyici / Patron</option>
            </select>
          </div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-4 py-2 border border-[#E7DECC] rounded-sm text-sm font-medium hover:bg-[#EFE7D6] transition-colors duration-150">İptal</button>
            <button type="submit" disabled={saving} data-testid="user-form-save" className="px-6 py-2 bg-[#B8933F] hover:bg-[#9C7A33] text-white rounded-sm text-sm font-medium transition-colors duration-150 disabled:opacity-60">{saving ? "Kaydediliyor..." : "Kaydet"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
